import posthog from 'posthog-js';
import 'posthog-js/dist/recorder';

class AnalyticsService {
  constructor() {
    this.initialized = false;
    this.enabled = false;
    this.sessionId = this._generateSessionId();
  }

  _generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  _getDeviceInfo() {
    return {
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language
    };
  }

  _getAppVersion() {
    return process.env.REACT_APP_VERSION || '1.0.0';
  }

  init() {
    const apiKey = process.env.REACT_APP_POSTHOG_KEY;
    // Use custom proxy to avoid ad blockers
    const apiHost = process.env.REACT_APP_POSTHOG_HOST || 'https://t.kata.fit';
    const uiHost = process.env.REACT_APP_POSTHOG_UI_HOST || 'https://us.posthog.com';

    if (!apiKey) {
      console.warn('[Analytics] PostHog API key not configured. Analytics disabled.');
      return;
    }

    try {
      posthog.init(apiKey, {
        api_host: apiHost,
        ui_host: uiHost,
        autocapture: false, // We'll manually track events for better control
        capture_pageview: true,
        capture_pageleave: true,
        persistence: 'localStorage',
        loaded: (posthog) => {
          if (process.env.NODE_ENV === 'development') {
            // posthog.opt_out_capturing(); // Disabled for testing // Don't track in dev by default
            console.log('[Analytics] PostHog loaded (opt-out in development)');
          } else {
            console.log('[Analytics] PostHog loaded');
          }
        }
      });

      this.initialized = true;
      this.enabled = true;
    } catch (error) {
      console.error('[Analytics] Failed to initialize PostHog:', error);
    }
  }

  /**
   * Track a custom event with enriched metadata
   * @param {string} event - Event name
   * @param {object} properties - Event properties
   */
  track(event, properties = {}) {
    if (!this.enabled || !this.initialized) return;

    try {
      posthog.capture(event, {
        ...properties,
        timestamp: new Date().toISOString(),
        session_id: this.sessionId,
        device_info: this._getDeviceInfo(),
        app_version: this._getAppVersion()
      });
    } catch (error) {
      console.error('[Analytics] Failed to track event:', event, error);
    }
  }

  /**
   * Identify a user
   * @param {string} userId - Unique user ID
   * @param {object} traits - User traits/properties
   */
  identify(userId, traits = {}) {
    if (!this.enabled || !this.initialized) return;

    try {
      posthog.identify(userId, traits);
    } catch (error) {
      console.error('[Analytics] Failed to identify user:', error);
    }
  }

  /**
   * Reset user identity (on logout)
   */
  reset() {
    if (!this.enabled || !this.initialized) return;

    try {
      posthog.reset();
      this.sessionId = this._generateSessionId(); // Generate new session on logout
    } catch (error) {
      console.error('[Analytics] Failed to reset:', error);
    }
  }

  /**
   * Set user properties
   * @param {object} properties - Properties to set
   */
  setUserProperties(properties) {
    if (!this.enabled || !this.initialized) return;

    try {
      posthog.people.set(properties);
    } catch (error) {
      console.error('[Analytics] Failed to set user properties:', error);
    }
  }

  /**
   * Track onboarding events
   */
  onboarding = {
    started: () => {
      this.track('onboarding_started');
    },
    
    slideViewed: (slideId, slideIndex, totalSlides) => {
      this.track('onboarding_slide_viewed', {
        slide_id: slideId,
        slide_index: slideIndex,
        total_slides: totalSlides,
        progress_percent: Math.round((slideIndex / totalSlides) * 100)
      });
    },

    coachAvatarSelected: (avatar) => {
      this.track('onboarding_coach_avatar_selected', { avatar_emoji: avatar });
    },

    coachStyleSelected: (styleId, styleLabel) => {
      this.track('onboarding_coach_style_selected', {
        style_id: styleId,
        style_label: styleLabel
      });
    },

    healthInfoSubmitted: (healthConditions) => {
      this.track('onboarding_health_info_submitted', {
        health_conditions: healthConditions,
        has_conditions: healthConditions.length > 0
      });
    },

    dietInfoSubmitted: (dietPreferences) => {
      this.track('onboarding_diet_info_submitted', {
        diet_preferences: dietPreferences,
        has_preferences: dietPreferences.length > 0
      });
    },

    profileCompleted: () => {
      this.track('profile_completed');
      this.setUserProperties({ profile_completed: true });
    },

    completed: (duration_ms) => {
      this.track('onboarding_completed', {
        duration_seconds: Math.round(duration_ms / 1000)
      });
      this.setUserProperties({ onboarded: true });
    },

    abandoned: (lastSlideId, slideIndex) => {
      this.track('onboarding_abandoned', {
        last_slide_id: lastSlideId,
        last_slide_index: slideIndex
      });
    }
  };

  /**
   * Track workout events (enhanced with granular tracking)
   */
  workout = {
    created: (workoutId, exerciseCount) => {
      this.track('workout_created', {
        workout_id: workoutId,
        exercise_count: exerciseCount
      });
    },

    started: (workoutId, workoutType, plannedDurationMin, isFirst = false) => {
      this.track('workout_started', {
        workout_id: workoutId,
        workout_type: workoutType,
        planned_duration_min: plannedDurationMin,
        is_first_workout: isFirst
      });
    },

    exerciseStarted: (workoutId, exerciseId, exerciseName, setNumber) => {
      this.track('workout_exercise_started', {
        workout_id: workoutId,
        exercise_id: exerciseId,
        exercise_name: exerciseName,
        set_number: setNumber
      });
    },

    exerciseCompleted: (workoutId, exerciseId, exerciseName, setsCompleted, reps, weight, rpe = null) => {
      this.track('workout_exercise_completed', {
        workout_id: workoutId,
        exercise_id: exerciseId,
        exercise_name: exerciseName,
        sets_completed: setsCompleted,
        reps: reps,
        weight: weight,
        rpe: rpe // Rate of Perceived Exertion (1-10)
      });
    },

    prAchieved: (workoutId, exerciseId, exerciseName, previousBest, newRecord, recordType) => {
      this.track('workout_pr_achieved', {
        workout_id: workoutId,
        exercise_id: exerciseId,
        exercise_name: exerciseName,
        previous_best: previousBest,
        new_record: newRecord,
        record_type: recordType // e.g., '1RM', 'max_reps', 'max_volume'
      });
    },

    restTimerStarted: (workoutId, exerciseId, durationSeconds) => {
      this.track('workout_rest_timer_started', {
        workout_id: workoutId,
        exercise_id: exerciseId,
        duration_seconds: durationSeconds
      });
    },

    restTimerCompleted: (workoutId, exerciseId, actualDurationSeconds) => {
      this.track('workout_rest_timer_completed', {
        workout_id: workoutId,
        exercise_id: exerciseId,
        actual_duration_seconds: actualDurationSeconds
      });
    },

    templateUsed: (workoutId, templateId, templateName) => {
      this.track('workout_template_used', {
        workout_id: workoutId,
        template_id: templateId,
        template_name: templateName
      });
    },

    completed: (workoutId, workoutType, actualDurationMin, exercisesCompleted, totalSets, isFirst = false) => {
      this.track('workout_completed', {
        workout_id: workoutId,
        workout_type: workoutType,
        actual_duration_min: actualDurationMin,
        exercises_completed: exercisesCompleted,
        total_sets: totalSets
      });

      if (isFirst) {
        this.track('first_workout_completed', {
          workout_id: workoutId,
          actual_duration_min: actualDurationMin
        });
        this.setUserProperties({ first_workout_completed_at: new Date().toISOString() });
      }
    },

    skipped: (workoutId, workoutType, skipReason = null) => {
      this.track('workout_skipped', {
        workout_id: workoutId,
        workout_type: workoutType,
        skip_reason: skipReason
      });
    },

    abandoned: (workoutId, progressPercent) => {
      this.track('workout_abandoned', {
        workout_id: workoutId,
        progress_percent: progressPercent
      });
    },

    exerciseAdded: (exerciseId, exerciseName) => {
      this.track('exercise_added_to_workout', {
        exercise_id: exerciseId,
        exercise_name: exerciseName
      });
    }
  };

  /**
   * Track routine events
   */
  routine = {
    started: (routineId, routineName) => {
      this.track('routine_started', {
        routine_id: routineId,
        routine_name: routineName
      });

      // Funnel tracking
      const firstRoutineStarted = localStorage.getItem('first_routine_started');
      if (!firstRoutineStarted) {
        this.track('first_routine_started', {
          routine_id: routineId,
          routine_name: routineName
        });
        localStorage.setItem('first_routine_started', 'true');
        this.setUserProperties({ first_routine_started_at: new Date().toISOString() });
      }
    },

    completed: (routineId, routineName, durationMin) => {
      this.track('routine_completed', {
        routine_id: routineId,
        routine_name: routineName,
        duration_min: durationMin
      });
    },

    abandoned: (routineId, routineName, completionPercentage) => {
      this.track('routine_abandoned', {
        routine_id: routineId,
        routine_name: routineName,
        completion_percentage: completionPercentage
      });
    }
  };

  /**
   * Track meal/nutrition events (enhanced)
   */
  meal = {
    logged: (mealType, calories, proteinG, carbsG, fatG, foodCount, isFirst = false) => {
      this.track('meal_logged', {
        meal_type: mealType, // breakfast, lunch, dinner, snack
        calories: calories,
        protein_g: proteinG,
        carbs_g: carbsG,
        fat_g: fatG,
        food_count: foodCount
      });

      if (isFirst) {
        this.track('first_meal_logged', {
          meal_type: mealType,
          calories: calories
        });
        this.setUserProperties({ first_meal_logged_at: new Date().toISOString() });
      }
    },

    foodItemAdded: (foodName, servingSize, source) => {
      this.track('food_item_added', {
        food_name: foodName,
        serving_size: servingSize,
        source: source // manual, search, barcode
      });
    },

    photoAdded: (mealType) => {
      this.track('meal_photo_added', {
        meal_type: mealType
      });
    },

    macrosHit: (mealType, targetProtein, targetCarbs, targetFat, actualProtein, actualCarbs, actualFat) => {
      const proteinHit = Math.abs(actualProtein - targetProtein) <= (targetProtein * 0.1); // Within 10%
      const carbsHit = Math.abs(actualCarbs - targetCarbs) <= (targetCarbs * 0.1);
      const fatHit = Math.abs(actualFat - targetFat) <= (targetFat * 0.1);
      
      this.track('meal_macros_hit', {
        meal_type: mealType,
        target_protein_g: targetProtein,
        target_carbs_g: targetCarbs,
        target_fat_g: targetFat,
        actual_protein_g: actualProtein,
        actual_carbs_g: actualCarbs,
        actual_fat_g: actualFat,
        protein_hit: proteinHit,
        carbs_hit: carbsHit,
        fat_hit: fatHit,
        all_macros_hit: proteinHit && carbsHit && fatHit
      });
    },

    planFollowed: (planId, planName, adherencePercent) => {
      this.track('meal_plan_followed', {
        plan_id: planId,
        plan_name: planName,
        adherence_percent: adherencePercent
      });
    }
  };

  /**
   * Track user action events
   */
  action = {
    buttonClick: (buttonId, buttonLocation, ctaText) => {
      this.track('button_click', {
        button_id: buttonId,
        button_location: buttonLocation,
        cta_text: ctaText
      });
    },

    formSubmit: (formName, formLocation, success) => {
      this.track('form_submit', {
        form_name: formName,
        form_location: formLocation,
        success: success
      });
    },

    searchPerformed: (query, resultsCount, filterApplied = null) => {
      this.track('search_performed', {
        query: query,
        results_count: resultsCount,
        filter_applied: filterApplied
      });
    },

    profileUpdated: (fieldChanged) => {
      this.track('profile_updated', {
        field_changed: fieldChanged // e.g., "avatar", "bio", "goals"
      });
    },

    settingsChanged: (settingKey, newValue) => {
      this.track('settings_changed', {
        setting_key: settingKey,
        new_value: newValue
      });
    }
  };

  /**
   * Track social/friend events
   */
  social = {
    friendAdded: (friendId, source) => {
      this.track('friend_added', {
        friend_id: friendId,
        source: source // search, invite, dojo
      });

      // Funnel tracking
      const firstFriendAdded = localStorage.getItem('first_friend_added');
      if (!firstFriendAdded) {
        this.track('first_friend_added', {
          friend_id: friendId
        });
        localStorage.setItem('first_friend_added', 'true');
        this.setUserProperties({ first_friend_added_at: new Date().toISOString() });
      }
    },

    friendRemoved: (friendId) => {
      this.track('friend_removed', {
        friend_id: friendId
      });
    },

    workoutShared: (workoutId, platform = null) => {
      this.track('share_workout', {
        workout_id: workoutId,
        platform: platform // if external (e.g., twitter, instagram)
      });
    },

    postCreated: () => {
      this.track('social_post_created');
    }
  };

  /**
   * Track dojo (community) events
   */
  dojo = {
    joined: (dojoId, dojoName, isFirst = false) => {
      this.track('dojo_joined', {
        dojo_id: dojoId,
        dojo_name: dojoName
      });

      if (isFirst) {
        this.track('first_dojo_joined', {
          dojo_id: dojoId,
          dojo_name: dojoName
        });
        this.setUserProperties({ first_dojo_joined_at: new Date().toISOString() });
      }
    },

    left: (dojoId, dojoName) => {
      this.track('dojo_left', {
        dojo_id: dojoId,
        dojo_name: dojoName
      });
    },

    leaderboardViewed: (context, period) => {
      this.track('leaderboard_viewed', {
        context: context, // 'dojo'
        period: period // 'week' or 'month'
      });
    }
  };

  /**
   * Track notification events
   */
  notification = {
    received: (notificationType) => {
      this.track('notification_received', {
        notification_type: notificationType
      });
    },

    clicked: (notificationType) => {
      this.track('notification_clicked', {
        notification_type: notificationType
      });
    }
  };

  /**
   * Track plan events
   */
  plan = {
    created: (planType, durationDays) => {
      this.track('plan_created', {
        plan_type: planType,
        duration_days: durationDays
      });
    },

    wizardAbandoned: (step) => {
      this.track('plan_wizard_abandoned', { step });
    }
  };

  /**
   * Track feature usage
   */
  feature = {
    recommendationViewed: (recommendationType) => {
      this.track('recommendation_viewed', {
        recommendation_type: recommendationType
      });
    },

    recommendationAccepted: (recommendationType) => {
      this.track('recommendation_accepted', {
        recommendation_type: recommendationType
      });
    },

    statsViewed: () => {
      this.track('stats_viewed');
    },

    gymSelected: (gymId, gymName) => {
      this.track('gym_selected', {
        gym_id: gymId,
        gym_name: gymName
      });
    },

    firstUsed: (featureName) => {
      const storageKey = `feature_first_used_${featureName}`;
      if (!localStorage.getItem(storageKey)) {
        this.track('feature_first_used', {
          feature_name: featureName
        });
        localStorage.setItem(storageKey, 'true');
        this.setUserProperties({ [`first_used_${featureName}`]: new Date().toISOString() });
      }
    }
  };

  /**
   * Track session events
   */
  session = {
    started: () => {
      this.track('app_opened');
    },

    userLogin: () => {
      this.track('user_login');
    },

    signUp: (method) => {
      this.track('sign_up', {
        method: method // e.g., email, google, apple
      });
    }
  };

  /**
   * Track retention checkpoints
   * Call this on app initialization to track active days
   */
  retention = {
    trackActiveDay: (daysSinceSignup) => {
      const checkpoints = [1, 7, 30];
      
      checkpoints.forEach(checkpoint => {
        if (daysSinceSignup === checkpoint) {
          const storageKey = `day_${checkpoint}_active_tracked`;
          if (!localStorage.getItem(storageKey)) {
            this.track(`day_${checkpoint}_active`);
            localStorage.setItem(storageKey, 'true');
          }
        }
      });
    }
  };

  /**
   * Track funnel events (critical conversion points)
   */
  funnel = {
    onboardingCompleted: (stepTimes) => {
      this.track('funnel_onboarding_completed', {
        step_times: stepTimes, // e.g., { intro: 10, health: 20, coach: 15, ... }
        total_time_seconds: Object.values(stepTimes).reduce((a, b) => a + b, 0)
      });
    },

    firstWorkoutCompleted: (timeToFirstWorkout) => {
      this.track('funnel_first_workout_completed', {
        time_to_first_workout_hours: timeToFirstWorkout / (1000 * 60 * 60),
        time_to_first_workout_days: timeToFirstWorkout / (1000 * 60 * 60 * 24)
      });
    },

    firstMealLogged: (timeToFirstMeal) => {
      this.track('funnel_first_meal_logged', {
        time_to_first_meal_hours: timeToFirstMeal / (1000 * 60 * 60),
        time_to_first_meal_days: timeToFirstMeal / (1000 * 60 * 60 * 24)
      });
    },

    reactivated: (daysSinceLastActive) => {
      if (daysSinceLastActive >= 7) {
        this.track('funnel_reactivated', {
          days_since_last_active: daysSinceLastActive
        });
      }
    }
  };

  /**
   * Track streak events
   */
  streak = {
    started: (currentStreak) => {
      if (currentStreak === 7) {
        const storageKey = 'streak_7_day_tracked';
        if (!localStorage.getItem(storageKey)) {
          this.track('streak_started', {
            streak_days: currentStreak
          });
          localStorage.setItem(storageKey, 'true');
          this.setUserProperties({ first_7_day_streak_at: new Date().toISOString() });
        }
      }
    },

    broken: (previousStreak) => {
      this.track('streak_broken', {
        previous_streak_days: previousStreak
      });
    },

    milestone: (streakDays) => {
      const milestones = [7, 14, 30, 60, 90, 180, 365];
      if (milestones.includes(streakDays)) {
        this.track('streak_milestone', {
          streak_days: streakDays
        });
      }
    }
  };

  /**
   * Track weekly goal events
   */
  weeklyGoal = {
    set: (goalType, targetValue) => {
      this.track('weekly_goal_set', {
        goal_type: goalType, // e.g., workouts, meals, calories_burned
        target_value: targetValue
      });
    },

    hit: (goalType, targetValue, actualValue) => {
      this.track('weekly_goal_hit', {
        goal_type: goalType,
        target_value: targetValue,
        actual_value: actualValue,
        over_target_percent: Math.round(((actualValue - targetValue) / targetValue) * 100)
      });
    },

    missed: (goalType, targetValue, actualValue) => {
      this.track('weekly_goal_missed', {
        goal_type: goalType,
        target_value: targetValue,
        actual_value: actualValue,
        completion_percent: Math.round((actualValue / targetValue) * 100)
      });
    }
  };

  /**
   * Track coach/AI companion events
   */
  coach = {
    messageSent: (messageLength, hasMedia = false) => {
      this.track('chat_message_sent', {
        message_length: messageLength,
        has_media: hasMedia
      });
    },

    recommendationClicked: (recommendationType, recommendationId) => {
      this.track('chat_recommendation_clicked', {
        recommendation_type: recommendationType,
        recommendation_id: recommendationId
      });
    },

    feedbackGiven: (messageId, feedbackType) => {
      this.track('chat_feedback_given', {
        message_id: messageId,
        feedback_type: feedbackType // thumbs_up, thumbs_down
      });
    },

    conversationStarted: () => {
      const storageKey = 'first_coach_conversation';
      if (!localStorage.getItem(storageKey)) {
        this.track('chat_first_conversation');
        localStorage.setItem(storageKey, 'true');
        this.setUserProperties({ first_coach_conversation_at: new Date().toISOString() });
      }
    }
  };
}

const analytics = new AnalyticsService();
export default analytics;
